import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "@/hooks/useCart";

type Props = {
  variant?: "overlay" | "solid";
};

export default function SiteHeader({ variant = "overlay" }: Props) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const cart = useCart();
  const location = useLocation();

  useEffect(() => {
    if (variant === "solid") return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [variant]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const solid = variant === "solid" || scrolled;

  const links = [
    { label: "Shop", to: "/shop" },
    { label: "Technology", to: "/technology" },
    { label: "Our Story", to: "/our-story" },
    { label: "Journal", to: "/journal" },
    { label: "Contact", to: "/contact" },
  ];

  const headerEl = (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200/50"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-5 md:px-10 h-[72px] md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center cursor-pointer">
          <span className={`text-[13px] md:text-[15px] font-medium tracking-[0.24em] ${solid ? "text-foreground-100" : "text-foreground-50"}`}>
            NORTHVALE
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9 absolute left-1/2 -translate-x-1/2">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className={`text-[12px] tracking-[0.02em] transition cursor-pointer whitespace-nowrap ${
                solid ? "text-foreground-200 hover:text-foreground-100" : "text-foreground-100/90 hover:text-foreground-50"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className={`hidden md:flex items-center gap-4 ${solid ? "text-foreground-200" : "text-foreground-100/90"}`}>
          <Link to="/shop" aria-label="Search" className="w-8 h-8 flex items-center justify-center hover:text-foreground-50 cursor-pointer">
            <i className="ri-search-line text-[16px]"></i>
          </Link>
          <Link to="/account" aria-label="Account" className="w-8 h-8 flex items-center justify-center hover:text-foreground-50 cursor-pointer">
            <i className="ri-user-line text-[16px]"></i>
          </Link>
          <button
            onClick={cart.open}
            aria-label="Open bag"
            className="w-8 h-8 flex items-center justify-center hover:text-foreground-50 cursor-pointer relative"
          >
            <i className="ri-shopping-bag-line text-[16px]"></i>
            {cart.count > 0 && (
              <span className="absolute -top-1 -right-1 text-[10px] w-4 h-4 flex items-center justify-center rounded-full bg-primary-500 text-foreground-50">
                {cart.count}
              </span>
            )}
          </button>
        </div>

        <button
          className={`md:hidden w-11 h-11 -mr-2 flex items-center justify-center cursor-pointer ${solid ? "text-foreground-100" : "text-foreground-50"}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-5 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <Link key={l.label} to={l.to} className="text-sm text-foreground-100 py-2.5 cursor-pointer">
              {l.label}
            </Link>
          ))}
          <button
            onClick={() => { setMobileOpen(false); cart.open(); }}
            className="text-sm text-foreground-100 py-2.5 text-left cursor-pointer"
          >
            Bag ({cart.count})
          </button>
        </div>
      )}
    </header>
  );

  if (typeof document === "undefined") return headerEl;
  return createPortal(headerEl, document.body);
}